import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, switchMap } from 'rxjs';
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class WebauthnService {
  private readonly base = `${environment.backendUrl}/webauthn`;

  constructor(private http: HttpClient) {}

  isSupported(): boolean {
    return browserSupportsWebAuthn();
  }

  register(label: string = 'Face ID'): Observable<any> {
    return this.http.post<any>(`${this.base}/register/options`, {}).pipe(
      switchMap((res) =>
        from(startRegistration({ optionsJSON: res.data })).pipe(
          switchMap((attResp) =>
            this.http.post(`${this.base}/register/verify`, { response: attResp, label })
          )
        )
      )
    );
  }

  // Authenticate with email (email-specific flow, challenge stored on user doc)
  authenticate(email: string): Observable<any> {
    return this.http.post<any>(`${this.base}/authenticate/options`, { email }).pipe(
      switchMap((res) =>
        from(startAuthentication({ optionsJSON: res.data })).pipe(
          switchMap((assertResp) =>
            this.http.post(`${this.base}/authenticate/verify`, { email, response: assertResp })
          )
        )
      )
    );
  }

  // Discoverable credential flow: no email needed — browser selects credential
  authenticateDiscoverable(): Observable<any> {
    return this.http.post<any>(`${this.base}/authenticate/options`, {}).pipe(
      switchMap((res) => {
        const challenge = res.data.challenge;
        return from(startAuthentication({ optionsJSON: res.data })).pipe(
          switchMap((assertResp) =>
            this.http.post(`${this.base}/authenticate/verify`, {
              response: assertResp,
              expectedChallenge: challenge,
            })
          )
        );
      })
    );
  }

  listCredentials(): Observable<any> {
    return this.http.get(`${this.base}/credentials`);
  }

  removeCredential(credentialId: string): Observable<any> {
    return this.http.delete(`${this.base}/credentials/${credentialId}`);
  }
}
